if (!customElements.get('tksc-global-menu')) {
  customElements.define(
    'tksc-global-menu',
    class TkscGlobalMenu extends HTMLElement {
      connectedCallback() {
        this.headerObserver = new ResizeObserver(() => {
          document.documentElement.style.setProperty(
            '--tksc-sticky-header-height',
            `${this.getBoundingClientRect().height}px`
          );
        });
        this.headerObserver.observe(this);
        if (this.initialized) {
          window.addEventListener('resize', this.handleResize);
          return;
        }

        this.initialized = true;
        this.triggers = new Map(
          Array.from(this.querySelectorAll('[data-global-menu-trigger]')).map((trigger) => [
            trigger.dataset.globalMenuTrigger,
            trigger,
          ])
        );
        this.panels = new Map(
          Array.from(this.querySelectorAll('[data-global-menu-panel]')).map((panel) => [
            panel.dataset.globalMenuPanel,
            panel,
          ])
        );
        this.activeKey = null;
        this.requestedKey = null;
        this.processing = false;
        this.handleResize = () => {
          if (this.activeKey) this.alignPanel(this.activeKey);
        };

        this.triggers.forEach((trigger, key) => {
          trigger.addEventListener('click', () => this.requestMenu(key));
        });
        this.addEventListener('keydown', (event) => this.handleKeydown(event));
        window.addEventListener('resize', this.handleResize);
      }

      disconnectedCallback() {
        this.headerObserver?.disconnect();
        document.documentElement.style.removeProperty('--tksc-sticky-header-height');
        window.removeEventListener('resize', this.handleResize);
      }

      requestMenu(key) {
        this.requestedKey = this.activeKey === key ? null : key;
        this.processQueue();
      }

      async processQueue() {
        if (this.processing) return;

        this.processing = true;

        try {
          while (this.activeKey !== this.requestedKey) {
            const targetKey = this.requestedKey;

            if (this.activeKey) await this.closeMenu(this.activeKey);
            if (targetKey && targetKey === this.requestedKey) await this.openMenu(targetKey);
          }
        } finally {
          this.processing = false;
        }
      }

      async openMenu(key) {
        const trigger = this.triggers.get(key);
        const panel = this.panels.get(key);
        if (!trigger || !panel || trigger.disabled) return;

        this.alignPanel(key);
        panel.hidden = false;
        panel.inert = false;
        panel.getBoundingClientRect();
        trigger.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');
        panel.classList.add('is-open');
        this.activeKey = key;

        await this.waitForAnimation(240);
      }

      alignPanel(key) {
        const trigger = this.triggers.get(key);
        const panel = this.panels.get(key);
        const stage = panel?.parentElement;
        if (!trigger || !panel || !stage) return;

        const triggerRect = trigger.getBoundingClientRect();
        const stageRect = stage.getBoundingClientRect();
        const left = Math.max(0, triggerRect.left - stageRect.left);
        const maxWidth = Math.max(0, stageRect.right - triggerRect.left);

        panel.style.setProperty('--tksc-global-submenu-left', `${left}px`);
        panel.style.setProperty('--tksc-global-submenu-max-width', `${maxWidth}px`);
      }

      async closeMenu(key) {
        const trigger = this.triggers.get(key);
        const panel = this.panels.get(key);
        if (!trigger || !panel) return;

        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
        panel.classList.remove('is-open');
        panel.inert = true;

        await this.waitForAnimation(240);

        panel.hidden = true;
        this.activeKey = null;
      }

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.activeKey) return;

        const trigger = this.triggers.get(this.activeKey);
        this.requestedKey = null;
        this.processQueue();
        trigger?.focus();
      }

      waitForAnimation(duration) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();

        return new Promise((resolve) => window.setTimeout(resolve, duration));
      }
    }
  );
}
